// ── VendorPicker ─────────────────────────────────────────────────────────────
//
// Reusable vendor selector that REPLACES every free-text vendor input across
// the app. Stops the next round of vendor-name drift at the source:
// "Casa Design" / "CASA DESIGN" / "CASA DESIGN GROUP" as three independent
// strings going into QB can't happen if every vendor input forces selection
// from the canonical directory.
//
// Behavior:
//   - Typeahead filters the vendor list as the user types
//   - No free text accepted — typing a string that doesn't match a vendor
//     does NOT update onChange. Field reverts on blur if nothing selected.
//   - "Add new vendor" option at the bottom (when allowAdd=true) opens a
//     two-stage modal: duplicate check (fuzzy match against existing
//     vendors with item + PO counts), then add form.
//   - Selected state shows the vendor name with a small ✕ to clear and
//     re-pick.
//
// Vendor renamed after item creation: out of scope for this commit. If a
// vendor record is renamed in the directory after an item references its
// old name, the picker shows the orphan in a warning state ("Unrecognized
// vendor — re-select to fix") rather than silently breaking. The user
// can either re-select from the directory or, if the rename was a typo,
// rename the directory entry back.

import React, { useState, useEffect, useRef, useMemo } from 'react';
import Fuse from 'fuse.js';
import { Lbl, Inp, Sel, Btn, Row, IS } from './ui';
import { matchesVendorNameOnly } from '../utils';

var COLORS = {
  border:    '#D8D7D2',
  borderHi:  '#999',
  text:      '#111111',
  textMute:  '#888',
  bg:        '#FFFFFF',
  bgHover:   '#F5F5F3',
  warn:      '#BF6800',
  warnBg:    '#FFF8E1',
  success:   '#1A5C38',
};

// Fuzzy-match normalizer. Strips business suffixes (LLC, Inc, Studio,
// Group, Co), collapses "&"/"and"/"+", normalizes whitespace, lowercases.
// "Casa Design, LLC" and "casa design group" both reduce to "casa design".
export function normalize(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[&+]| and /g, ' ')
    .replace(/\b(llc|inc|incorporated|co|corp|corporation|ltd|studio|group|company)\b\.?/g, '')
    .replace(/[.,]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Builds the count maps for items + POs by exact vendor.name. Fast — runs
// once when the modal opens, against the in-memory lists already loaded
// by AppContext. No API call.
function buildCounts(items, purchaseOrders) {
  var itemCounts = {};
  var poCounts = {};
  (items || []).forEach(function(it) {
    var v = it.vendor;
    if (v) itemCounts[v] = (itemCounts[v] || 0) + 1;
  });
  (purchaseOrders || []).forEach(function(p) {
    var v = p.vendorName || p.vendor_name;
    if (v) poCounts[v] = (poCounts[v] || 0) + 1;
  });
  return { itemCounts: itemCounts, poCounts: poCounts };
}

// ── DUPLICATE-CHECK MODAL ────────────────────────────────────────────────────

// Exported so ImportWizard can reuse the same two-stage check (typeahead
// fuzzy match → confirm-new add form) when resolving unrecognized vendor
// names from bulk import. Same UX guarantees: cannot add until matches
// acknowledged; concurrent-add collision guarded; ESC closes.
export function DuplicateCheckModal(p) {
  var [stage, setStage] = useState('check');           // 'check' | 'add'
  var [typedName, setTypedName] = useState(p.initialName || '');
  var [acknowledged, setAcknowledged] = useState(false);
  var [saveErr, setSaveErr] = useState('');
  var [saving, setSaving] = useState(false);
  var [newVendor, setNewVendor] = useState({
    name: '', vendorType: 'Vendor', contact: '', email: '', phone: '',
    website: '', address: '', address2: '', city: '', state: '', zip: '', notes: '',
  });

  // ESC closes.
  useEffect(function() {
    function onKey(e) { if (e.key === 'Escape' && !saving && p.onClose) p.onClose(); }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [saving, p.onClose]);

  var counts = useMemo(function() {
    return buildCounts(p.items || [], p.purchaseOrders || []);
  }, [p.items, p.purchaseOrders]);

  // Fuse instance — built once per vendor list. Token-based + threshold
  // wide enough to surface "Casa Design, LLC" against typed "Casa Design".
  var fuse = useMemo(function() {
    return new Fuse(p.vendors || [], {
      keys: ['name', '_normalized'],
      threshold: 0.4,
      includeScore: true,
      ignoreLocation: true,
      minMatchCharLength: 2,
    });
  }, [p.vendors]);

  // Run two passes: raw fuzzy on the original name, and normalized-form
  // fuzzy on the stripped-suffix form. Merge and dedupe by id. Top 5.
  var matches = useMemo(function() {
    var q = (typedName || '').trim();
    if (q.length < 2) return [];
    var norm = normalize(q);
    var raw = fuse.search(q).map(function(r) { return { v: r.item, score: r.score }; });
    var withNorm = (p.vendors || []).map(function(v) {
      return Object.assign({}, v, { _normalized: normalize(v.name) });
    });
    var fuseNorm = new Fuse(withNorm, {
      keys: ['_normalized'], threshold: 0.5, includeScore: true,
      ignoreLocation: true, minMatchCharLength: 2,
    });
    var byNorm = fuseNorm.search(norm).map(function(r) { return { v: r.item, score: r.score }; });
    var merged = {};
    raw.concat(byNorm).forEach(function(m) {
      var prev = merged[m.v.id];
      if (!prev || m.score < prev.score) merged[m.v.id] = m;
    });
    return Object.values(merged)
      .sort(function(a, b) { return a.score - b.score; })
      .slice(0, 5)
      .map(function(m) { return m.v; });
  }, [typedName, fuse, p.vendors]);

  // Sync newVendor.name to typedName whenever the user adjusts Stage 1.
  useEffect(function() {
    setNewVendor(function(prev) { return Object.assign({}, prev, { name: typedName }); });
  }, [typedName]);

  function pickExisting(v) {
    if (p.onPick) p.onPick(v.name);
    if (p.onClose) p.onClose();
  }

  async function submitAdd() {
    setSaveErr('');
    var name = String(newVendor.name || '').trim();
    if (!name) { setSaveErr('Vendor name is required.'); return; }
    if (!newVendor.vendorType) { setSaveErr('Vendor type is required.'); return; }
    var collision = (p.vendors || []).find(function(v) { return v.name === name; });
    if (collision) {
      // Concurrent-add guard — another caller added the same canonical
      // name between Stage 1 and Stage 2 submit. Surface and let the
      // user pick the now-existing vendor.
      setSaveErr('A vendor with this exact name already exists. Use "Cancel" then select it from the dropdown.');
      return;
    }
    setSaving(true);
    try {
      var payload = Object.assign({}, newVendor, { name: name });
      // Caller's onAddVendor is responsible for assigning an id and
      // persisting. We just hand back the new record.
      if (p.onAddVendor) await p.onAddVendor(payload);
      if (p.onPick) p.onPick(name);
      setSaving(false);
      if (p.onClose) p.onClose();
    } catch (err) {
      setSaving(false);
      setSaveErr('Save failed: ' + (err && err.message ? err.message : 'unknown error'));
    }
  }

  var backdrop = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  var card = {
    width: 560, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto',
    background: COLORS.bg, borderRadius: 8, padding: 24,
    boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
    fontFamily: "'DM Sans', sans-serif", color: COLORS.text,
  };
  var heading = { fontSize: 16, fontWeight: 600, marginBottom: 4 };
  var sub = { fontSize: 12, color: COLORS.textMute, marginBottom: 16 };

  if (stage === 'check') {
    return (
      <div style={backdrop} data-testid="vendor-dup-modal" onClick={function(e) { if (e.target === e.currentTarget && p.onClose) p.onClose(); }}>
        <div style={card}>
          <div style={heading}>Add a new vendor?</div>
          <div style={sub}>Before adding, check that the vendor isn't already in the directory under a slightly different name. Duplicates create reconciliation work later.</div>

          <Lbl required>What's the vendor's name?</Lbl>
          <input
            data-testid="vendor-dup-input"
            value={typedName}
            onChange={function(e) { setTypedName(e.target.value); setAcknowledged(false); }}
            autoFocus
            placeholder="e.g. Casa Design"
            style={Object.assign({}, IS, { fontSize: 14, padding: '8px 12px' })} />

          {matches.length > 0 && (
            <div style={{ marginTop: 16 }} data-testid="vendor-dup-matches">
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMute, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
                Closest matches in your directory
              </div>
              {matches.map(function(v) {
                var itemCt = counts.itemCounts[v.name] || 0;
                var poCt   = counts.poCounts[v.name]   || 0;
                return (
                  <button
                    key={v.id}
                    data-testid={'vendor-dup-match-' + v.id}
                    onClick={function() { pickExisting(v); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '10px 12px', marginBottom: 6,
                      background: COLORS.bg, border: '0.5px solid ' + COLORS.border, borderRadius: 6,
                      fontFamily: 'inherit', cursor: 'pointer',
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{v.name}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMute, marginTop: 2, display: 'flex', gap: 12 }}>
                      <span>{itemCt} item{itemCt === 1 ? '' : 's'}</span>
                      <span>{poCt} PO{poCt === 1 ? '' : 's'}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {typedName.trim().length >= 2 && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: COLORS.warnBg, border: '0.5px solid #F5C800', borderRadius: 6, fontSize: 11, color: COLORS.warn }}>
              Selecting an existing vendor above prevents duplicate vendor records, sidemark drift, and PO mismatch errors. Only confirm "new vendor" if you've checked the matches above.
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 11, color: COLORS.textMute, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginRight: 'auto' }}>
              <input
                type="checkbox"
                data-testid="vendor-dup-ack"
                checked={acknowledged}
                onChange={function(e) { setAcknowledged(e.target.checked); }}
                disabled={typedName.trim().length < 2} />
              I've checked the matches above
            </label>
            <Btn variant="secondary" onClick={p.onClose}>Cancel</Btn>
            <Btn
              data-testid="vendor-dup-confirm-new"
              onClick={function() { setStage('add'); }}
              disabled={!acknowledged || typedName.trim().length < 2}>
              Confirm: new vendor →
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  // Stage 2 — add form. Pre-filled name from Stage 1.
  function setField(k) { return function(v) { setNewVendor(function(prev) { var n = Object.assign({}, prev); n[k] = v; return n; }); }; }
  return (
    <div style={backdrop} data-testid="vendor-add-modal" onClick={function(e) { if (e.target === e.currentTarget && !saving && p.onClose) p.onClose(); }}>
      <div style={card}>
        <div style={heading}>New vendor</div>
        <div style={sub}>Will be added to the directory and selected on the current item.</div>

        <Inp label="Vendor Name" value={newVendor.name} onChange={setField('name')} required={true} />

        <Row>
          <div style={{ flex: 1 }}>
            <Sel label="Vendor Type" value={newVendor.vendorType} onChange={setField('vendorType')} required={true}>
              <option value="Vendor">Vendor</option>
              <option value="Workroom">Workroom</option>
              <option value="Receiver">Receiver</option>
              <option value="Installer">Installer</option>
              <option value="Tradesperson">Tradesperson</option>
              <option value="Other">Other</option>
            </Sel>
          </div>
          <div style={{ flex: 2 }}>
            <Inp label="Contact" value={newVendor.contact} onChange={setField('contact')} placeholder="Primary contact name" />
          </div>
        </Row>

        <Row>
          <div style={{ flex: 2 }}><Inp label="Email" value={newVendor.email} onChange={setField('email')} placeholder="contact@vendor.com" /></div>
          <div style={{ flex: 1 }}><Inp label="Phone" value={newVendor.phone} onChange={setField('phone')} placeholder="(415) 555-0000" /></div>
        </Row>

        <Inp label="Website" value={newVendor.website} onChange={setField('website')} placeholder="vendor.com" />

        <Inp label="Address" value={newVendor.address} onChange={setField('address')} />
        <Inp label="Address 2" value={newVendor.address2} onChange={setField('address2')} />

        <Row>
          <div style={{ flex: 2 }}><Inp label="City" value={newVendor.city} onChange={setField('city')} /></div>
          <div style={{ flex: 1 }}><Inp label="State" value={newVendor.state} onChange={setField('state')} /></div>
          <div style={{ flex: 1 }}><Inp label="ZIP" value={newVendor.zip} onChange={setField('zip')} /></div>
        </Row>

        <Inp label="Notes" value={newVendor.notes} onChange={setField('notes')} placeholder="Internal notes (optional)" />

        {saveErr && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: '#FEE', border: '0.5px solid #C8322A', borderRadius: 6, fontSize: 12, color: '#C8322A' }}>
            {saveErr}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <Btn variant="secondary" onClick={function() { setStage('check'); setSaveErr(''); }} disabled={saving}>← Back</Btn>
          <Btn variant="secondary" onClick={p.onClose} disabled={saving}>Cancel</Btn>
          <Btn data-testid="vendor-add-save" onClick={submitAdd} disabled={saving || !newVendor.name.trim()}>
            {saving ? 'Adding…' : 'Add Vendor'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function VendorPicker(p) {
  var [query, setQuery]       = useState('');
  var [open, setOpen]         = useState(false);
  var [modalOpen, setModalOpen] = useState(false);
  var ref = useRef(null);

  var value = p.value || '';
  var vendors = p.vendors || [];
  var allowAdd = p.allowAdd !== false;
  var placeholder = p.placeholder || 'Select vendor…';
  var required = !!p.required;

  // Close dropdown on click outside.
  useEffect(function() {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery('');  // Revert typed text — no free-text persistence
      }
    }
    if (open) document.addEventListener('mousedown', onDoc);
    return function() { document.removeEventListener('mousedown', onDoc); };
  }, [open]);

  // Selected vendor record (if value matches one in the directory).
  var selectedVendor = useMemo(function() {
    if (!value) return null;
    return vendors.find(function(v) { return v.name === value; }) || null;
  }, [value, vendors]);

  // value is set but no matching vendor — orphan state. Render warning.
  var isOrphan = value && !selectedVendor;

  // Filtered list. Uses matchesVendorNameOnly from utils.js — same
  // name-only matcher VendorDirectory uses. The picker is selecting a
  // vendor by name, so multi-field substring (matchesVendorSearch) is
  // out of scope. Single-char queries work; punctuation-tolerant
  // ("OG" / "O+G" / "O and G" all match "O+G Studios"). 8-item cap is
  // presentation-only.
  var filtered = useMemo(function() {
    var q = query.trim();
    if (!q) return vendors.slice(0, 8);
    return vendors.filter(function(v) { return matchesVendorNameOnly(v, q); }).slice(0, 8);
  }, [query, vendors]);

  function selectExisting(name) {
    if (p.onChange) p.onChange(name);
    setOpen(false);
    setQuery('');
  }

  function clear() {
    if (p.onChange) p.onChange('');
    setQuery('');
    setOpen(true);
  }

  function openAddModal() {
    setOpen(false);
    setModalOpen(true);
  }

  async function handleAddVendor(newVendor) {
    if (p.onAddVendor) await p.onAddVendor(newVendor);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  // Selected state — show vendor name + clear button. Click to re-pick.
  var selectedDisplay = selectedVendor || isOrphan ? (
    <div
      data-testid="vendor-picker-selected"
      style={Object.assign({}, IS, {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: isOrphan ? COLORS.warnBg : COLORS.bg,
        borderColor: isOrphan ? '#F5C800' : COLORS.border,
        cursor: 'default',
      })}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ color: isOrphan ? COLORS.warn : COLORS.text, fontWeight: isOrphan ? 600 : 400 }}>
          {value}
        </span>
        {isOrphan && (
          <span style={{ fontSize: 10, color: COLORS.warn, marginTop: 2 }}>
            Not in vendor directory — re-select to fix
          </span>
        )}
      </div>
      <button
        type="button"
        data-testid="vendor-picker-clear"
        onClick={clear}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 14, color: COLORS.textMute, padding: '0 4px',
        }}
        title="Change vendor">
        ✕
      </button>
    </div>
  ) : null;

  // Dropdown — typeahead matches + "Add new vendor" footer.
  var hasAnyMatch = filtered.length > 0;
  var dropdown = open && !selectedDisplay ? (
    <div
      data-testid="vendor-picker-dropdown"
      style={{
        position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2,
        background: COLORS.bg, border: '0.5px solid ' + COLORS.border, borderRadius: 6,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto',
        zIndex: 100, fontFamily: "'DM Sans', sans-serif",
      }}>
      {filtered.map(function(v) {
        return (
          <div
            key={v.id}
            data-testid={'vendor-picker-option-' + v.id}
            onMouseDown={function() { selectExisting(v.name); }}
            style={{
              padding: '8px 12px', fontSize: 13, cursor: 'pointer',
              borderBottom: '0.5px solid #EFEFEC', color: COLORS.text,
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = COLORS.bgHover; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = COLORS.bg; }}>
            <div style={{ fontWeight: 500 }}>{v.name}</div>
            {(v.contact || v.vendorType) && (
              <div style={{ fontSize: 10, color: COLORS.textMute, marginTop: 2 }}>
                {[v.vendorType, v.contact].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        );
      })}
      {!hasAnyMatch && query && (
        <div style={{ padding: '10px 12px', fontSize: 12, color: COLORS.textMute, fontStyle: 'italic' }}>
          No vendors match "{query}".
        </div>
      )}
      {!hasAnyMatch && !query && vendors.length === 0 && (
        <div style={{ padding: '10px 12px', fontSize: 12, color: COLORS.textMute, fontStyle: 'italic' }}>
          Vendor directory is empty.
        </div>
      )}
      {allowAdd && (
        <button
          type="button"
          data-testid="vendor-picker-add"
          onMouseDown={openAddModal}
          style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '10px 12px', fontSize: 12, color: COLORS.success, fontWeight: 600,
            background: '#F5F8F5', border: 'none', borderTop: '0.5px solid ' + COLORS.border,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
          + Add new vendor{query ? ' ("' + query + '")' : ''}
        </button>
      )}
    </div>
  ) : null;

  return (
    <div ref={ref} style={{ position: 'relative', marginBottom: 12 }} data-testid="vendor-picker">
      {p.label !== null && <Lbl required={required}>{p.label || 'Vendor'}</Lbl>}
      {/*
        Lock-mode contract: every material field site in ItemForm renders
        a node carrying data-field="<fieldName>". In editable mode the
        node must be INPUT/SELECT/TEXTAREA (the lock-mode parameterized
        test in __tests__/itemFormLockMode.test.jsx asserts this). The
        typeahead input below already carries the data-field hook, but
        when a value is set we render the selected-display div instead,
        so the input falls out of the DOM. This hidden input preserves
        the contract regardless of state — it's the editable handle's
        durable identity. Locked rendering in ItemForm uses a separate
        read-only div with data-field="vendor" (not a VendorPicker).
      */}
      <input type="hidden" data-field="vendor" value={value || ''} readOnly />
      {selectedDisplay || (
        <input
          data-testid="vendor-picker-input" autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
          type="text"
          value={query}
          onChange={function(e) { setQuery(e.target.value); setOpen(true); }}
          onFocus={function() { setOpen(true); }}
          onKeyDown={function(e) {
            // Enter on an exact-name match selects it. Otherwise no-op —
            // never persist typed text without a directory match.
            if (e.key === 'Enter') {
              var exact = vendors.find(function(v) { return String(v.name).toLowerCase() === query.trim().toLowerCase(); });
              if (exact) selectExisting(exact.name);
              else e.preventDefault();
            } else if (e.key === 'Escape') {
              setOpen(false);
              setQuery('');
            }
          }}
          placeholder={placeholder}
          style={IS} />
      )}
      {dropdown}
      {modalOpen && (
        <DuplicateCheckModal
          vendors={vendors}
          items={p.items || []}
          purchaseOrders={p.purchaseOrders || []}
          initialName={query}
          onPick={selectExisting}
          onAddVendor={handleAddVendor}
          onClose={function() { setModalOpen(false); setQuery(''); }} />
      )}
    </div>
  );
}
